import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, map, mapTo, switchMap } from 'rxjs/operators';

import { labelMaxLength, labelMinLength } from '../../app.constants';
import { CoreApiService, queryAll } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { getApiErrorMessage } from '../../shared/errors';
import { Session } from '../../shared/models/session.model';
import { Subject as FwSubject } from '../../shared/models/subject.model';
import { firstValueFrom, replayValue } from '../../shared/observable.utils';
import { generateElementId } from '../../shared/string.utils';
import { SessionService } from '../session.service';

type SessionInput = Pick<Session, '_id' | 'label'>

interface DialogData {
  projectId?: string
  sessions: SessionInput[]
}

interface SessionResult extends SessionInput {
  error?: string
}

interface MoveSummary {
  total: number
  moved: number
  failed: number
  failedSessions: SessionResult[]
}

@Component({
  selector: 'move-sessions-to-subject-dialog',
  styleUrls: ['./move-sessions-to-subject-dialog.component.scss'],
  templateUrl: './move-sessions-to-subject-dialog.component.html',
})
export class MoveSessionsToSubjectDialog implements OnDestroy {
  elementIds = {
    existingSubjectLabel: generateElementId(),
    newSubjectLabel: generateElementId(),
    projectLabel: generateElementId(),
  }
  filteredSubjects$: Observable<FwSubject[]>
  form: FormGroup
  groupedProjects$ = this.projectsService.getGroupedProjects([PermissionAction.ContainersCreateHierarchy])
  moveSummary: MoveSummary = null
  newSubjectLabelExists$: Observable<boolean>
  projectSubjects$ = new BehaviorSubject<FwSubject[]>([])
  saving = false
  sessions = this.data.sessions

  private subscriptions = {
    subjects: Subscription.EMPTY,
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: DialogData,
    private coreApi: CoreApiService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
    private sessionsService: SessionService,
  ) {
    this.form = new FormGroup({
      projectId: new FormControl(this.data.projectId || null, [Validators.required]),
      existingOrNew: new FormControl('existing'),
      existingSubjectId: new FormControl(null, [Validators.required, this.validSubjectId]),
      newSubjectLabel: new FormControl(
        { value: '', disabled: true },
        [Validators.required, Validators.minLength(labelMinLength), Validators.maxLength(labelMaxLength)]
      ),
    });

    this.form.get('existingOrNew').valueChanges.subscribe(selection => {
      const existingId = this.form.get('existingSubjectId');
      const newLabel = this.form.get('newSubjectLabel');
      if (selection === 'new') {
        existingId.disable();
        newLabel.enable();
      } else {
        existingId.enable();
        newLabel.disable();
      }
    });

    this.subscriptions.subjects = replayValue(this.form.get('projectId')).pipe(
      switchMap(projectId => projectId
        ? this.profileService.siteAdmin$.pipe(
          switchMap(isSiteAdmin => queryAll(params => this.coreApi.projects.subjects(projectId, {
            ...params,
            ...(isSiteAdmin ? { exhaustive: true } : {}),
          })))
        ) : of([]))
    ).subscribe(this.projectSubjects$);

    this.filteredSubjects$ = combineLatest([
      this.projectSubjects$,
      replayValue(this.form.get('existingSubjectId')).pipe(debounceTime(200)),
    ]).pipe(
      map(([allSubjects, labelInput]) => {
        if (!labelInput) {
          return allSubjects;
        }
        // labelInput could be a user label input or selected option ID
        const subjectById = allSubjects.find(subject => subject._id === labelInput);
        if (subjectById) {
          return [subjectById];
        }
        const lowerLabel = labelInput.toLowerCase();
        return allSubjects.filter(subject => (subject.label || subject._id || '').toLowerCase().includes(lowerLabel));
      }),
      map(filteredSubjects => filteredSubjects.slice(0, 20)), // render first 20 options
    );

    this.newSubjectLabelExists$ = replayValue(this.form.get('newSubjectLabel')).pipe(debounceTime(200)).pipe(
      map(newLabel => Boolean(newLabel) && this.projectSubjects$.value.some(subject => subject.label === newLabel)),
    );
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  displaySubjectLabel = (subjectId?: string): string => {
    const subject = this.projectSubjects$.value.find(sub => sub._id === subjectId);
    return subject?.label || subject?._id || '';
  }

  /**
   * If the user types a label without selecting an option, convert the label to an ID if possible
   */
  blurSubjectLabel(): void {
    const input = this.form.get('existingSubjectId').value;
    if (!input || this.projectSubjects$.value.some(sub => sub._id === input)) {
      return;
    }
    const subject = this.projectSubjects$.value.find(sub => sub.label === input);
    if (subject) {
      this.form.get('existingSubjectId').setValue(subject._id);
    }
  }

  async moveSessions(): Promise<void> {
    if (!this.form.valid) {
      return;
    }
    this.saving = true;
    const projectId = this.form.get('projectId').value;

    let subjectId = this.form.get('existingSubjectId').value;
    if (this.form.get('newSubjectLabel').enabled) {
      const subjectLabel = this.form.get('newSubjectLabel').value;
      const existingSubject = this.projectSubjects$.value.find(subject => subject.label === subjectLabel);
      if (existingSubject) {
        subjectId = existingSubject._id;
      } else {
        const createSubject$ = this.coreApi.subjects.post({
          code: subjectLabel,
          label: subjectLabel,
          project: projectId,
        });
        try {
          subjectId = (await firstValueFrom(createSubject$))._id;
        } catch (error) {
          this.logger.error(error);
          this.notifications.error('session.addSubject.error');
          this.saving = false;
          return;
        }
      }
    }

    this.form.disable();
    const moveSessions$: Observable<(SessionInput & { error: Error })[]> = forkJoin(
      this.sessions.map(session => this.coreApi.sessions.put(session._id, { subject: subjectId }).pipe(
        mapTo({ ...session, error: null }),
        catchError((error: Error) => of({ ...session, error })),
      ))
    );
    const results = await firstValueFrom(moveSessions$);
    this.saving = false;

    const errors = results.filter(result => result.error);
    if (errors.length > 0) {
      this.logger.error(errors[0].error);
    }

    this.sessionsService.clearCheckedSessions();

    this.moveSummary = {
      total: this.sessions.length,
      moved: this.sessions.length - errors.length,
      failed: errors.length,
      failedSessions: errors.map(session => ({
        ...session,
        error: getApiErrorMessage(session.error) || session.error.message,
      })),
    };
  }

  private validSubjectId: ValidatorFn = control => {
    if (control.value && !this.projectSubjects$.value.some(subject => subject._id === control.value)) {
      return { validSubjectId: 'Subject does not exist in project' };
    }
    return null;
  }
}
